import { type CityGraph, EDGE_BRIDGE, EDGE_TUNNEL, EDGE_STAIR, NODE_PORT } from './citygraph.js';
import { buildStreetSurface, roundStreetPath, type StreetPath } from './streetSurface.js';
/** Preserve graph IDs, ports and bed elevations. Only coverage bends within the
 * existing graded corridor; bridges and stairs retain their engineered geometry. */
export function graphStreetSurface(g:Pick<CityGraph,'nodeU'|'nodeV'|'nodeKind'|'edgeA'|'edgeB'|'edgeKind'>,widthOf:(edge:number)=>number){
  const incident:number[][]=Array.from({length:g.nodeU.length},()=>[]),used=new Set<number>(),paths:StreetPath[]=[];
  const eligible=(e:number)=>g.edgeKind[e]!==EDGE_BRIDGE&&g.edgeKind[e]!==EDGE_TUNNEL;
  for(let e=0;e<g.edgeA.length;e++)if(eligible(e)){incident[g.edgeA[e]].push(e);incident[g.edgeB[e]].push(e);}
  const terminal=(n:number,e:number)=>g.nodeKind[n]===NODE_PORT||incident[n].length!==2||incident[n].some(v=>g.edgeKind[v]!==g.edgeKind[e]||widthOf(v)!==widthOf(e));
  for(let e=0;e<g.edgeA.length;e++){
    if(!eligible(e)||used.has(e))continue;
    let start=g.edgeA[e];
    // Find the end of a degree-two chain before emitting it. Closed rings keep
    // their lowest edge as the stable start.
    let prev=e,n=start;const scanned=new Set<number>();
    while(!terminal(n,prev)){
      const next=incident[n].find(v=>v!==prev)!;
      if(scanned.has(next)||next===e)break;scanned.add(next);
      n=g.edgeA[next]===n?g.edgeB[next]:g.edgeA[next];prev=next;start=n;
    }
    const nodes=[start];let node=start,edge=incident[start].includes(e)?e:prev;
    for(;;){
      if(used.has(edge))break;used.add(edge);
      node=g.edgeA[edge]===node?g.edgeB[edge]:g.edgeA[edge];nodes.push(node);
      if(node===start||terminal(node,edge))break;
      edge=incident[node].find(v=>v!==edge)!;
    }
    const points=nodes.map(n=>({x:g.nodeU[n],y:0,z:g.nodeV[n]})),width=widthOf(e);
    if(points.length>1){
      const rounded=g.edgeKind[e]===EDGE_STAIR?points:roundStreetPath(points,width*.45,4);
      // Legacy graphs can contain coincident nodes after edge splitting.
      // They have no surface area; keep their graph identity but emit no strip.
      if(rounded.length>1)paths.push({id:`edge:${e}`,width,points:rounded});
    }
  }
  return buildStreetSurface(paths);
}
